import React, { useState } from 'react';
import {
  Box, Typography, Paper, Divider, ToggleButtonGroup,
  ToggleButton, Select, MenuItem, FormControl, Switch, Button,
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import LanguageIcon from '@mui/icons-material/Language';
import InfoIcon from '@mui/icons-material/Info';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useTranslation } from 'react-i18next';
import { useUiSettings } from '@/contexts/UiSettingsContext.tsx';
import i18n from '@/i18n/i18n.ts';
import { TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import { IDOC_LABELS_EVENT } from '@components/debug/IDocInspector.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';

const languages = [
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'en', name: 'English',    flag: '🇬🇧' },
  { code: 'pl', name: 'Polski',     flag: '🇵🇱' },
  { code: 'be', name: 'Беларуская', flag: '🇧🇾' },
  { code: 'cs', name: 'Čeština',   flag: '🇨🇿' },
  { code: 'es', name: 'Español',   flag: '🇪🇸' },
  { code: 'ru', name: 'Русский',   flag: '🇷🇺' },
];

interface SettingRowProps {
  icon: React.ReactNode;
  label: string;
  description?: string;
  control: React.ReactNode;
}

const SettingRow: React.FC<SettingRowProps> = ({ icon, label, description, control }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, gap: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="body2" fontWeight={500}>{label}</Typography>
        {description && (
          <Typography variant="caption" color="text.secondary">{description}</Typography>
        )}
      </Box>
    </Box>
    <Box sx={{ flexShrink: 0 }}>{control}</Box>
  </Box>
);

export const InterfaceSettingsTab: React.FC = () => {
  const { t } = useTranslation();
  const {
    mode, fontScale, hintsEnabled,
    toggleColorMode, toggleFontScale, toggleHints, resetHints,
  } = useUiSettings();

  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'uk');
  const [idocLabels, setIdocLabels] = useState(() => localStorage.getItem('idoc-labels-enabled') === 'true');
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const handleLanguageChange = (code: string) => {
    setLanguage(code);
    localStorage.setItem('language', code);
    i18n.changeLanguage(code);
  };

  const handleIdocLabelsToggle = (checked: boolean) => {
    setIdocLabels(checked);
    localStorage.setItem('idoc-labels-enabled', String(checked));
    window.dispatchEvent(new Event(IDOC_LABELS_EVENT));
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, ml: 1, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
        {t('settings.interface.general')}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-i-doc={TabIndices.SETTINGS_INTERFACE}>
        <SettingRow
          icon={mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          label={t('settings.interface.theme.label')}
          description={t('settings.interface.theme.description')}
          control={
            <ToggleButtonGroup
              value={mode}
              exclusive
              size="small"
              onChange={(_, v) => { if (v) toggleColorMode(); }}
            >
              <ToggleButton value="light">{t('settings.interface.theme.light')}</ToggleButton>
              <ToggleButton value="dark">{t('settings.interface.theme.dark')}</ToggleButton>
            </ToggleButtonGroup>
          }
        />

        <Divider />

        <SettingRow
          icon={<FormatSizeIcon />}
          label={t('settings.interface.fontSize.label')}
          description={t('settings.interface.fontSize.description')}
          control={
            <ToggleButtonGroup
              value={fontScale}
              exclusive
              size="small"
              onChange={(_, v) => { if (v) toggleFontScale(); }}
            >
              <ToggleButton value="normal">{t('settings.interface.fontSize.normal')}</ToggleButton>
              <ToggleButton value="compact">{t('settings.interface.fontSize.compact')}</ToggleButton>
            </ToggleButtonGroup>
          }
        />

        <Divider />

        <SettingRow
          icon={<LanguageIcon />}
          label={t('settings.interface.language.label')}
          control={
            <FormControl size="small">
              <Select
                value={language}
                onChange={(e) => handleLanguageChange(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                {languages.map(lang => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.flag}&nbsp;&nbsp;{lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        />

        <Divider />

        <SettingRow
          icon={<InfoIcon />}
          label={t('settings.interface.hints.label')}
          description={t('settings.interface.hints.description')}
          control={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setResetConfirmOpen(true)}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                {t('settings.interface.hints.restore')}
              </Button>
              <Switch
                checked={hintsEnabled}
                onChange={() => toggleHints()}
                color="primary"
              />
            </Box>
          }
        />
      </Paper>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, ml: 1, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
        {t('settings.interface.developer')}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <SettingRow
          icon={<TerminalIcon />}
          label={t('settings.interface.idocLabels.label')}
          description={t('settings.interface.idocLabels.description')}
          control={
            <Switch
              checked={idocLabels}
              onChange={(e) => handleIdocLabelsToggle(e.target.checked)}
              color="primary"
            />
          }
        />
      </Paper>

      <ConfirmationDialog
        open={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        onConfirm={() => {
          resetHints();
          setResetConfirmOpen(false);
        }}
        title={t('settings.interface.hints.confirmTitle')}
        message={t('settings.interface.hints.confirmMessage')}
        confirmText={t('settings.interface.hints.confirmAction')}
        cancelText={t('settings.interface.hints.cancelAction')}
      />
    </Box>
  );
};
